import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { calculateTotalAcres, calculateAcres } from "../utils/mapCalculations";
import { computeSubtotal, moveSection, moveItem } from "@/utils/lineItemSections";

const useQuoteForm = () => {
  const { quoteId } = useParams();
  const isEditing = !!quoteId;
  const navigate = useNavigate();

  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calculatingDistance, setCalculatingDistance] = useState(false);

  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [propertyAddress, setPropertyAddress] = useState("");
  const [parcelPin, setParcelPin] = useState("");
  const [additionalProperties, setAdditionalProperties] = useState([]);
  const [notes, setNotes] = useState("");
  const [notesImage, setNotesImage] = useState(null);
  const [expectedStartDate, setExpectedStartDate] = useState("");
  const [startDateType, setStartDateType] = useState("first_available");
  const [validDays, setValidDays] = useState(30);
  const [lineItems, setLineItems] = useState([]);
  const [polygons, setPolygons] = useState([]);
  const [totalAcres, setTotalAcres] = useState(0);
  const [distanceInfo, setDistanceInfo] = useState(null);
  const [baseLocation, setBaseLocation] = useState(null);
  const [requireFullPayment, setRequireFullPayment] = useState(false);
  const [pinLookupResult, setPinLookupResult] = useState(null);
  const [quotePdfFilename, setQuotePdfFilename] = useState(null);
  const [discountType, setDiscountType] = useState(null); // "percentage" or "fixed"
  const [discountValue, setDiscountValue] = useState(null);
  const [pricingMode, setPricingMode] = useState("itemized"); // "itemized" | "lump_sum"
  const [lumpSumAmount, setLumpSumAmount] = useState(0);
  const [quoteStatus, setQuoteStatus] = useState(null);
  const [quoteSignature, setQuoteSignature] = useState(null); // { name, date } | null

  useEffect(() => {
    const acres = calculateTotalAcres(polygons);
    setTotalAcres(acres);
  }, [polygons]);

  // Keep any line item that's linked to a map area in sync with that area's acreage.
  useEffect(() => {
    setLineItems((prev) => {
      let changed = false;
      const next = prev.map((it) => {
        if (!it.area_link) return it;
        const acres = it.area_link === "__all__"
          ? calculateTotalAcres(polygons)
          : calculateAcres(polygons.find((p) => p._id === it.area_link));
        if (acres !== it.quantity) {
          changed = true;
          return { ...it, quantity: acres, total: acres * (it.unit_price || 0) };
        }
        return it;
      });
      return changed ? next : prev;
    });
  }, [polygons]);

  const fetchInitialData = useCallback(async () => {
    try {
      const [customersRes, servicesRes, estimatesRes, baseLocationRes] = await Promise.all([
        axios.get(`${API}/customers`).catch(() => ({ data: [] })),
        axios.get(`${API}/services`),
        axios.get(`${API}/estimates`).catch(() => ({ data: [] })),
        axios.get(`${API}/base-location`).catch(() => ({ data: null })),
      ]);
      
      setCustomers(customersRes.data);
      setServices(servicesRes.data);
      setEstimates(estimatesRes.data);
      if (baseLocationRes.data) {
        setBaseLocation(baseLocationRes.data);
      }
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const fetchQuote = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/quotes/${quoteId}`);
      const quote = response.data;
      
      setSelectedCustomer({
        id: quote.customer_id,
        name: quote.customer_name,
        email: quote.customer_email,
        phone: quote.customer_phone,
        address: quote.customer_address,
      });
      setPropertyAddress(quote.property_address || "");
      setParcelPin(quote.parcel_pin || "");
      setAdditionalProperties(quote.additional_properties || []);
      setNotes(quote.notes || "");
      setNotesImage(quote.notes_image || null);
      setQuotePdfFilename(quote.quote_pdf_filename || null);
      setExpectedStartDate(quote.expected_start_date || "");
      // Backwards compatibility: legacy quotes with only `expected_start_date` set
      // → treat as preferred. Quotes with neither set → default to first_available.
      setStartDateType(
        quote.start_date_type || (quote.expected_start_date ? "preferred" : "first_available")
      );
      setValidDays(quote.valid_days ?? 30);
      setLineItems(quote.line_items || []);
      setDiscountType(quote.discount_type || null);
      setDiscountValue(quote.discount_value || null);
      setPricingMode(quote.pricing_mode || "itemized");
      setLumpSumAmount(Number(quote.lump_sum_amount) || 0);
      setQuoteStatus(quote.status || null);
      setQuoteSignature(
        quote.signature_name
          ? { name: quote.signature_name, date: quote.signature_date }
          : null
      );
      
      if (quote.polygon_data) {
        const arr = Array.isArray(quote.polygon_data) ? quote.polygon_data : [quote.polygon_data];
        setPolygons(arr.map((p) => ({ ...p, _id: p._id || `area-${crypto.randomUUID()}` })));
      }
      setRequireFullPayment(quote.require_full_payment || false);
    } catch (error) {
      toast.error("Failed to load quote");
      navigate("/");
    }
  }, [quoteId, navigate]);

  const lookupByPin = useCallback(async (pin, fallbackAddress) => {
    try {
      const response = await axios.get(`${API}/parcels/by-pin/${encodeURIComponent(pin)}`);
      const data = response.data;
      // NOTE: intentionally do NOT overwrite propertyAddress here — the customer's
      // own address is the source of truth. The parcel's site address (or a blank/
      // owner value) was clobbering the visible address on select. We only use the
      // lookup to center the map on the parcel.
      if (data.centroid) {
        setPinLookupResult(data);
      }
    } catch (error) {
      console.error("PIN lookup failed:", error);
    }
  }, []);

  const handleCustomerSelect = useCallback((customerId) => {
    const customer = customers.find((c) => c.id === customerId);
    if (customer) {
      setSelectedCustomer(customer);
      // The quote shows the customer's display address (falls back to the location address).
      setPropertyAddress(customer.display_address || customer.address || "");
      const source = customer.location_source || "address";
      if (customer.pin) {
        setParcelPin(customer.pin);
        // Don't run an address-geocode parcel lookup when Coordinates is authoritative.
        if (source !== "coordinates") lookupByPin(customer.pin, customer.address);
      }
    }
  }, [customers, lookupByPin]);

  const handleEstimateSelect = useCallback((estimateId) => {
    const estimate = estimates.find((e) => e.id === estimateId || e.estimate_id === estimateId);
    if (!estimate) return;

    setSelectedEstimate(estimate);
    if (estimate.customer_name) {
      setSelectedCustomer({
        id: estimate.customer_id || estimateId,
        name: estimate.customer_name,
        email: estimate.customer_email,
        phone: estimate.customer_phone,
        address: estimate.customer_address,
      });
    }
    if (estimate.property_address) {
      setPropertyAddress(estimate.property_address);
    }
    
    const polyData = estimate.polygons || estimate.polygon_data || estimate.geometry;
    if (polyData) {
      if (Array.isArray(polyData)) {
        setPolygons(polyData);
      } else {
        setPolygons([polyData]);
      }
    }
    
    if (estimate.line_items?.length > 0) {
      setLineItems(estimate.line_items.map(item => ({
        id: crypto.randomUUID(),
        service_name: item.service_name || item.name,
        description: item.description || "",
        quantity: item.quantity || 1,
        unit: item.unit || "each",
        unit_price: item.unit_price || item.price || 0,
        total: (item.quantity || 1) * (item.unit_price || item.price || 0)
      })));
    }

    const acres = estimate.acreage || estimate.total_acres || calculateTotalAcres(Array.isArray(polyData) ? polyData : [polyData]);
    if (acres > 0) {
      toast.success(`Estimate loaded: ${acres.toFixed(2)} acres`);
    }
  }, [estimates]);

  const calculateMobilizationFee = useCallback(async (address) => {
    if (!address || address.trim().length < 5) return;
    
    setCalculatingDistance(true);
    try {
      const response = await axios.post(`${API}/calculate-distance`, { destination: address });
      setDistanceInfo(response.data);
      
      const mobIndex = lineItems.findIndex(item => 
        item.service_name.toLowerCase().includes("mobilization")
      );
      
      if (mobIndex >= 0) {
        const updatedItems = [...lineItems];
        updatedItems[mobIndex] = {
          ...updatedItems[mobIndex],
          quantity: response.data.round_trip_miles,
          unit_price: 4.00,
          total: response.data.mobilization_fee
        };
        setLineItems(updatedItems);
        toast.success(`Mobilization fee updated: $${response.data.mobilization_fee.toFixed(2)}`);
      } else {
        toast.info(`Distance: ${response.data.round_trip_miles} mi round trip. Add Mobilization Fee to apply.`);
      }
    } catch (error) {
      console.error("Distance calculation failed:", error);
      toast.error("Could not calculate distance");
    } finally {
      setCalculatingDistance(false);
    }
  }, [lineItems]);

  const addSection = useCallback(() => {
    setLineItems(prev => [
      ...prev,
      {
        id: crypto.randomUUID(),
        is_section_header: true,
        service_name: "New Section",
        description: "",
        quantity: 0,
        unit: "",
        unit_price: 0,
        total: 0,
      },
    ]);
  }, []);

  const addLineItem = useCallback((serviceId, sectionHeaderId = null) => {
    if (serviceId === "custom") return "custom";

    const service = services.find((s) => s.id === serviceId);
    if (!service) return null;

    let newItem = {
      id: crypto.randomUUID(),
      service_name: service.name,
      description: service.description,
      quantity: 1,
      unit: service.default_unit,
      unit_price: service.default_price,
      total: service.default_price,
    };

    if (serviceId === "mobilization_fee" && distanceInfo) {
      newItem = { ...newItem, quantity: distanceInfo.round_trip_miles, unit_price: 4.00, total: distanceInfo.mobilization_fee };
      toast.success(`Mobilization: $${distanceInfo.mobilization_fee.toFixed(2)}`);
    }
    if (serviceId === "forestry_mulching" && totalAcres > 0) {
      newItem.quantity = totalAcres;
      newItem.unit = "acre";
      toast.info(`Acreage auto-filled: ${totalAcres} acres`);
    }

    setLineItems(prev => {
      if (!sectionHeaderId) return [...prev, newItem];
      // Insert before the next section header after sectionHeaderId
      const headerIdx = prev.findIndex(i => i.id === sectionHeaderId);
      if (headerIdx === -1) return [...prev, newItem];
      let insertIdx = prev.length;
      for (let i = headerIdx + 1; i < prev.length; i++) {
        if (prev[i].is_section_header) { insertIdx = i; break; }
      }
      const next = [...prev];
      next.splice(insertIdx, 0, newItem);
      return next;
    });
    return null;
  }, [services, distanceInfo, totalAcres]);

  const updateLineItem = useCallback((id, field, value) => {
    setLineItems(prev =>
      prev.map((item) => {
        if (item.id === id) {
          const updated = { ...item, [field]: value };
          if (field === "quantity" || field === "unit_price") {
            updated.total = updated.quantity * updated.unit_price;
          }
          return updated;
        }
        return item;
      })
    );
  }, []);

  const removeLineItem = useCallback((id) => {
    setLineItems(prev => prev.filter((item) => item.id !== id));
  }, []);

  const moveSectionBy = useCallback((headerId, dir) => {
    setLineItems(prev => moveSection(prev, headerId, dir));
  }, []);

  const moveItemBy = useCallback((itemId, dir) => {
    setLineItems(prev => moveItem(prev, itemId, dir));
  }, []);

  // Link a line item's quantity to a drawn map area (or the total of all areas).
  const linkAreaToItem = useCallback((itemId, link) => {
    setLineItems((prev) => prev.map((it) => {
      if (it.id !== itemId) return it;
      if (!link) {
        const rest = { ...it };
        delete rest.area_link;
        return rest;
      }
      const acres = link === "__all__"
        ? calculateTotalAcres(polygons)
        : calculateAcres(polygons.find((p) => p._id === link));
      return { ...it, area_link: link, quantity: acres, total: acres * (it.unit_price || 0) };
    }));
  }, [polygons]);

  const renamePolygon = useCallback((index, name) => {
    setPolygons((prev) => prev.map((p, i) => (i === index ? { ...p, _name: name } : p)));
  }, []);

  const calculateSubtotal = useCallback(() => {
    // In lump-sum mode, the user-entered total IS the subtotal — line items still
    // render to customer as scope ("What's Included") but contribute no math.
    if (pricingMode === "lump_sum") {
      return Number(lumpSumAmount) || 0;
    }
    return computeSubtotal(lineItems);
  }, [lineItems, pricingMode, lumpSumAmount]);

  const handleSave = useCallback(async () => {
    if (!selectedCustomer) {
      toast.error("Please select a customer");
      return;
    }
    if (lineItems.length === 0) {
      toast.error("Please add at least one line item");
      return;
    }

    setSaving(true);
    const subtotal = calculateSubtotal();

    const quoteData = {
      customer_id: selectedCustomer.id,
      customer_name: selectedCustomer.name,
      customer_email: selectedCustomer.email || null,
      customer_phone: selectedCustomer.phone || null,
      customer_address: selectedCustomer.address || null,
      property_address: propertyAddress || null,
      parcel_pin: parcelPin || null,
      additional_properties: additionalProperties.length > 0 ? additionalProperties : null,
      line_items: lineItems,
      subtotal: subtotal,
      total: subtotal,
      pricing_mode: pricingMode || "itemized",
      lump_sum_amount: pricingMode === "lump_sum" ? Number(lumpSumAmount) : null,
      discount_type: discountType || null,
      discount_value: discountValue || null,
      notes: notes || null,
      notes_image: notesImage || null,
      expected_start_date: (startDateType === "first_available" || startDateType === "valid_for") ? null : (expectedStartDate || null),
      start_date_type: startDateType || "first_available",
      valid_days: startDateType === "valid_for" ? (validDays || 30) : null,
      polygon_data: polygons.length > 0 ? polygons : null,
      source_estimate_id: selectedEstimate?.id || null,
      require_full_payment: requireFullPayment,
    };

    try {
      if (isEditing) {
        await axios.put(`${API}/quotes/${quoteId}`, quoteData);
        toast.success("Quote updated");
      } else {
        const response = await axios.post(`${API}/quotes`, quoteData);
        toast.success("Quote created");
        navigate(`/quotes/${response.data.id}/edit`);
      }
    } catch (error) {
      console.error("Save quote error:", error.response?.data || error.message);
      const errorDetail = error.response?.data?.detail;
      if (Array.isArray(errorDetail)) {
        const messages = errorDetail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
        toast.error(`Validation error: ${messages}`);
      } else if (typeof errorDetail === 'string') {
        toast.error(errorDetail);
      } else {
        toast.error("Failed to save quote");
      }
    } finally {
      setSaving(false);
    }
  }, [selectedCustomer, lineItems, calculateSubtotal, propertyAddress, parcelPin, additionalProperties, notes, notesImage, expectedStartDate, startDateType, validDays, polygons, selectedEstimate, isEditing, quoteId, navigate, requireFullPayment, discountType, discountValue]);

  return {
    isEditing,
    quoteId,
    navigate,
    customers,
    services,
    estimates,
    loadingCustomers,
    saving,
    calculatingDistance,
    selectedCustomer,
    selectedEstimate,
    propertyAddress,
    setPropertyAddress,
    parcelPin,
    setParcelPin,
    additionalProperties,
    setAdditionalProperties,
    notes,
    setNotes,
    notesImage,
    setNotesImage,
    expectedStartDate,
    setExpectedStartDate,
    startDateType,
    setStartDateType,
    validDays,
    setValidDays,
    lineItems,
    setLineItems,
    polygons,
    setPolygons,
    totalAcres,
    distanceInfo,
    baseLocation,
    requireFullPayment,
    setRequireFullPayment,
    pinLookupResult,
    setPinLookupResult,
    quotePdfFilename,
    setQuotePdfFilename,
    discountType,
    setDiscountType,
    discountValue,
    setDiscountValue,
    pricingMode,
    setPricingMode,
    lumpSumAmount,
    setLumpSumAmount,
    quoteStatus,
    quoteSignature,
    fetchInitialData,
    fetchQuote,
    handleCustomerSelect,
    handleEstimateSelect,
    calculateMobilizationFee,
    addLineItem,
    addSection,
    updateLineItem,
    removeLineItem,
    moveSectionBy,
    moveItemBy,
    linkAreaToItem,
    renamePolygon,
    calculateSubtotal,
    handleSave,
  };
};

export default useQuoteForm;
